<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import UserName from '@/components/UserName.vue'
import TrackTitle from '@/components/TrackTitle.vue'
import { podiumClass, podiumColor } from '@/lib/podium'

/**
 * Standings with one column per track: the points every player took on each of them, and the total.
 *
 * Two places show this shape — the fastest lap standings and the standings of a season, which the
 * event page of a season opens on — and the only thing that changes between them is where the points
 * come from (a rule on the finishing positions on one side, the tables of the season on the other):
 * the table itself lives here, and the callers hand it rows already built.
 *
 * It only renders: loading, empty and error states stay with the caller.
 */
const props = defineProps({
  /**
   * Rows, best first: `{ id, name, total, wins, position, isMe, tracks: { [trackKey]: { points,
   * position } } }`. `position` is optional — the place in the list is used when it is missing — and a
   * track without an entry is shown as an empty cell.
   */
  rows: { type: Array, default: () => [] },
  /** Track keys, in the order the columns are shown. */
  trackKeys: { type: Array, default: () => [] },
  /** Points per finishing position: the legend under the table, nothing when the list is empty. */
  pointsPerPosition: { type: Array, default: () => [] },
})

const { t } = useI18n()

/** Place of a row: the one it carries, or the place it has in this list. */
const positionOf = (row, index) => row.position ?? index + 1

/** Legend of the points, e.g. "1° 10 · 2° 6 · 3° 4". */
const pointsLegend = computed(() =>
  props.pointsPerPosition.map((points, index) => `${index + 1}° ${points}`).join(' · ')
)
</script>

<template>
  <!-- A season can have many tracks: on a narrow screen the table scrolls sideways instead of
       squeezing every column. -->
  <div class="table-scroll">
    <v-table hover>
      <thead>
        <tr>
          <th class="text-center" style="width: 56px">{{ t('standings.position') }}</th>
          <th>{{ t('leaderboard.driver') }}</th>
          <!-- The points come first, before the tracks they were earned on: the total is the number
               that matters, and on a narrow screen it is there without scrolling. -->
          <th class="text-center">{{ t('leaderboard.total') }}</th>
          <th v-for="key in trackKeys" :key="key" class="text-center text-no-wrap">
            <TrackTitle :track-id="key" variant="stacked" :flag="false" />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, index) in rows"
          :key="row.id"
          :class="{ 'top-entry': positionOf(row, index) <= 3, 'me-row': row.isMe }"
        >
          <td class="text-center">
            <v-icon v-if="positionOf(row, index) <= 3" :class="podiumClass(positionOf(row, index))" size="20">
              mdi-medal
            </v-icon>
            <span v-else class="text-medium-emphasis">{{ positionOf(row, index) }}</span>
          </td>
          <td>
            <div class="d-flex align-center ga-2">
              <UserName :name="row.name" class="font-weight-medium" />
              <v-tooltip v-if="row.wins" :text="`${row.wins} ${t('leaderboard.wins')}`" location="top">
                <template v-slot:activator="{ props: tooltip }">
                  <v-chip
                    v-bind="tooltip"
                    size="x-small"
                    color="amber-darken-2"
                    variant="tonal"
                    prepend-icon="mdi-flag-checkered"
                  >
                    {{ row.wins }}
                  </v-chip>
                </template>
              </v-tooltip>
            </div>
          </td>
          <td class="text-center">
            <span class="font-weight-bold">{{ row.total }}</span>
          </td>
          <td v-for="key in trackKeys" :key="key" class="text-center">
            <v-chip
              v-if="row.tracks?.[key]"
              :color="podiumColor(row.tracks[key].position) ?? 'primary'"
              size="small"
              variant="tonal"
              label
            >
              {{ row.tracks[key].points }}
            </v-chip>
            <span v-else class="text-medium-emphasis">–</span>
          </td>
        </tr>
      </tbody>
    </v-table>
  </div>

  <template v-if="pointsPerPosition.length && rows.length">
    <v-divider />
    <div class="d-flex flex-wrap align-center ga-2 px-4 py-3">
      <v-icon size="16" color="primary">mdi-information-outline</v-icon>
      <span class="text-caption text-medium-emphasis">{{ t('leaderboard.pointsPerPosition') }}:</span>
      <span class="text-caption font-weight-medium">{{ pointsLegend }}</span>
    </div>
  </template>
</template>

<style scoped>
.table-scroll {
  overflow-x: auto;
}

/* The first three places are tinted, the logged-in player more: the two add up on their own row. */
.top-entry td {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.me-row td {
  background-color: rgba(var(--v-theme-primary), 0.06);
}
</style>
